import { Button, ButtonStyles, Dropdown, Icons, NestedSelectList, Search } from '@silverfort/sf-components';
import { FC, useCallback, useMemo, useState } from 'react';
import { AD_ENTITY_TYPES, HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../../../components/utils';
import { ColumnFilter, CustomOwnerData, ServiceAccountFilters } from '../ServiceAccountsTypes';
import { useServiceAccountContext } from '../contexts/ServiceAccountContext';
import { getServiceAccountFiltesWithLicense } from './statistics/FiltersUtils';
import { SmartPolicyWithEntitiesFullData } from '../screens/SmartPolicies/SmartPoliciesTypes';

const listEntitiesTypes = {
    [AD_ENTITY_TYPES.USER]: {
        tooltip: 'Account',
        icon: Icons.User
    },
    [AD_ENTITY_TYPES.GROUP]: {
        tooltip: 'Security group',
        icon: Icons.UserGroup
    }
};

const getUniqueItems = (allItems: { title: string; value: string }[]) => {
    const uniqueValues: Set<String> = new Set();
    return allItems.filter((item) => {
        if (uniqueValues.has(item.value)) {
            return false;
        }
        uniqueValues.add(item.value);
        return true;
    });
};

const ServiceAccountsFilters: FC = () => {
    const [searchedOwners, setSearchedOwners] = useState<
        { guid: string; name: string; domain: string; type: string }[]
    >([]);
    const [searchedCustomOwners, setSearchedCustomOwners] = useState<Omit<CustomOwnerData, 'added_by'>[]>([]);
    const [domainNames, setDomainNames] = useState<string[]>([]);
    const [searchedSmartPolicies, setSearchedSmartPolicies] = useState<SmartPolicyWithEntitiesFullData[]>([]);
    const {
        activeFilters,
        setActiveFilters,
        onPremNHILicenseISPMEnabled,
        onPremNHILicenseITDREnabled,
        onPremNHILicenseProtectionEnabled
    } = useServiceAccountContext();

    const allFiltersByLicense = useMemo(
        () =>
            getServiceAccountFiltesWithLicense(
                onPremNHILicenseITDREnabled,
                onPremNHILicenseISPMEnabled,
                onPremNHILicenseProtectionEnabled
            ),
        [onPremNHILicenseISPMEnabled, onPremNHILicenseITDREnabled, onPremNHILicenseProtectionEnabled]
    );

    const searchOwner = async (text: string) => {
        try {
            if (!text) {
                setSearchedOwners([]);
                return;
            }
            const owners: {
                data: { Results: { guid: string; name: string; domain_name: string; type: string }[] } | null;
            } = await httpGet(`/users/searchEntitiesByTypes/${text}`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        hideDeleted: true,
                        entitiesTypes: [AD_ENTITY_TYPES.USER, AD_ENTITY_TYPES.GROUP],
                        authTypes: ['ad']
                    }
                }
            });

            setSearchedOwners(
                owners.data?.Results.map(({ guid, name, domain_name, type }) => {
                    return { guid, name, domain: domain_name, type };
                }) || []
            );
        } catch (error) {
            console.error("failed getting the user's data from server!", error);
        }
    };

    const ownerItems = useMemo(() => {
        const selectedItems = (activeFilters.owner || []).map((owner) => {
            const [guid, name, domain, type] = owner.split(',');
            return {
                guid,
                name,
                domain,
                type
            };
        });

        if (searchedOwners.length + selectedItems.length === 0) {
            return [];
        }

        const allOwners = [...searchedOwners, ...selectedItems];
        const allItems = allOwners.map((owner) => ({
            title: owner.name,
            value: `${owner.guid},${owner.name},${owner.domain},${owner.type}`,
            subtitle: owner.domain,
            IconLeft: listEntitiesTypes[owner.type]?.icon
        }));

        return getUniqueItems(allItems);
    }, [searchedOwners, activeFilters.owner]);

    const searchCustomOwner = useCallback(async (text: string) => {
        try {
            if (!text) {
                setSearchedCustomOwners([]);
                return;
            }
            const customOwnersSearch: { data: { customOwners: Omit<CustomOwnerData, 'added_by'>[] } | null } =
                await httpGet(`/ServiceAccountsV2/searchCustomOwners/${text}`);
            const searchData = customOwnersSearch.data?.customOwners || [];
            setSearchedCustomOwners(searchData);
        } catch (error) {
            console.error("failed getting the custom owners' data from server!", error);
        }
    }, []);

    const customOwnersItems = useMemo(() => {
        const selectedItems = (activeFilters.custom_owner || []).map((owner: string) => {
            const [, ownerValue] = owner.split(',');
            return {
                title: ownerValue,
                value: owner
            };
        });
        const searchedItems = searchedCustomOwners.map((owner) => ({
            title: owner.owner_value,
            value: `${owner.owner_type},${owner.owner_value}`
        }));

        const allItems = [...selectedItems, ...searchedItems];
        return getUniqueItems(allItems);
    }, [searchedCustomOwners, activeFilters.custom_owner]);

    const searchDomain = async (domainNameSearch: string) => {
        try {
            const domains: { data: { domains: string[] } | null } = await httpGet(
                '/ServiceAccountsV2/domainsFiltered',
                {
                    [HTTP_OPTIONS.REQ_OPTIONS]: { params: { domainNameSearch } },
                    [HTTP_OPTIONS.HANDLE_ERRORS]: true
                }
            );
            setDomainNames(domains.data?.domains || []);
        } catch (error) {
            console.error("failed getting the domain's data from server!", error);
        }
    };

    const domainSearchItems = useMemo(() => {
        return Array.from(new Set([...(activeFilters.domain_name ?? []), ...domainNames])).map((domain: string) => ({
            title: domain,
            value: domain
        }));
    }, [domainNames, activeFilters.domain_name]);

    const searchSmartPolicies = async (text: string) => {
        try {
            if (!text) {
                setSearchedSmartPolicies([]);
                return;
            }
            const smartPolicies: { data: { smartPolicies: SmartPolicyWithEntitiesFullData[] } | null } = await httpGet(
                `/serviceAccountsV2/smartPolicies`,
                {
                    [HTTP_OPTIONS.REQ_OPTIONS]: { params: { filters: { name: text, showDeleted: true } } }
                }
            );
            const searchData = smartPolicies.data?.smartPolicies || [];
            setSearchedSmartPolicies(searchData);
        } catch (error) {
            console.error("failed getting the smart policies' data from server!", error);
        }
    };

    const smartPoliciesItems = useMemo(() => {
        const selectedItems = (activeFilters.smart_policies || []).map((policy: string) => {
            const [, name, isDeleted] = policy.split(',');
            return {
                title: name,
                value: policy,
                IconLeft: isDeleted === 'true' ? Icons.Block : undefined,
                tooltipDescription: isDeleted === 'true' ? 'Deleted' : undefined
            };
        });
        const searchedItems = searchedSmartPolicies.map((policy) => ({
            title: policy.name,
            value: `${policy.id},${policy.name},${policy.is_deleted}`,
            IconLeft: policy.is_deleted ? Icons.Block : undefined,
            tooltipDescription: policy.is_deleted ? 'Deleted' : undefined
        }));
        const allItems = [...selectedItems, ...searchedItems];
        return getUniqueItems(allItems);
    }, [searchedSmartPolicies, activeFilters.smart_policies]);

    const updateFilter = useCallback(
        (id: string, value: any) => {
            setActiveFilters((prevFilters) => ({ ...prevFilters, [id]: value }));

            if ((id === 'custom_owner' || id === 'owner') && value.length !== 0) {
                setActiveFilters((prevFilters) => {
                    let insightsFilterVal: undefined | string[] = undefined;
                    if (prevFilters.insights) {
                        insightsFilterVal = prevFilters.insights.filter((insight) => insight !== 'no_owner');
                        if (insightsFilterVal.length === 0) {
                            insightsFilterVal = undefined;
                        }
                    }

                    return {
                        ...prevFilters,
                        insights: insightsFilterVal
                    };
                });
            } else if (id === 'insights' && value.includes('no_owner')) {
                setActiveFilters((prevFilters) => ({
                    ...prevFilters,
                    owner: undefined,
                    custom_owner: undefined
                }));
            }
        },
        [setActiveFilters]
    );

    const removeFilter = useCallback(
        (id: keyof ServiceAccountFilters) => {
            setActiveFilters((prevFilters) => {
                const { [id]: _, ...newFilters } = prevFilters;
                return newFilters;
            });
        },
        [setActiveFilters]
    );

    const setSearch = (value: string) => {
        if (value) {
            updateFilter('display_name', value);
        } else {
            removeFilter('display_name');
        }
    };

    const selectedItems = useCallback(
        (filter: ColumnFilter) => activeFilters[filter.id] ?? (filter.multiple ? [] : undefined),
        [activeFilters]
    );

    const filters: Record<string, ColumnFilter> = useMemo(
        () =>
            Object.entries(allFiltersByLicense).reduce((acc, [key, filter]) => {
                const updatedFilter = {
                    ...filter,
                    selectedItems: selectedItems(filter),
                    removable: !!(filter.multiple ? activeFilters[filter.id]?.length : activeFilters[filter.id]),
                    onChange: (selectedItems) => {
                        if (selectedItems.length === 0) {
                            removeFilter(filter.id);
                        } else {
                            updateFilter(filter.id, selectedItems);
                        }
                    },
                    onRemove: () => removeFilter(filter.id)
                };
                if (key === 'owner') {
                    updatedFilter.searchHandler = searchOwner;
                    updatedFilter.items = ownerItems;
                    updatedFilter.onClose = () => setSearchedOwners([]);
                }
                if (key === 'custom_owner') {
                    updatedFilter.searchHandler = searchCustomOwner;
                    updatedFilter.items = customOwnersItems;
                    updatedFilter.onClose = () => setSearchedCustomOwners([]);
                }
                if (key === 'domain_name') {
                    updatedFilter.searchHandler = searchDomain;
                    updatedFilter.onClose = () => setDomainNames([]);
                    updatedFilter.items = domainSearchItems;
                }
                if (key === 'smart_policies') {
                    updatedFilter.searchHandler = searchSmartPolicies;
                    updatedFilter.items = smartPoliciesItems;
                    updatedFilter.onClose = () => setSearchedSmartPolicies([]);
                }

                acc[key] = updatedFilter;
                return acc;
            }, {}),
        [
            activeFilters,
            ownerItems,
            customOwnersItems,
            searchCustomOwner,
            domainSearchItems,
            smartPoliciesItems,
            removeFilter,
            selectedItems,
            updateFilter,
            allFiltersByLicense
        ]
    );

    const shownFilters = useMemo(() => {
        const selectedFilterIds = Object.keys(activeFilters);
        return Object.values(filters)
            .filter((filter) => filter.showFilter || activeFilters[filter.id])
            .sort((a, b) => {
                // sorting requirement is: selected filters first, by their order of activation, then the rest
                const aIndex = selectedFilterIds.indexOf(a.id);
                const bIndex = selectedFilterIds.indexOf(b.id);
                if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;
                return 0;
            });
    }, [filters, activeFilters]);

    const additionalFilters = useMemo(
        () =>
            Object.entries(filters).reduce((acc, [key, filter]) => {
                if (!filter.showFilter && !activeFilters[key]) {
                    const { onRemove, ...rest } = filter;
                    return { ...acc, [key]: { ...rest } };
                }
                return acc;
            }, {}),
        [filters, activeFilters]
    );

    return (
        <div className="flex justify-between flex-1">
            <div className="flex gap-2 flex-wrap">
                {shownFilters.map((filter, i) => (
                    <div key={filter.id}>
                        <Dropdown {...filter} disableDefaultCount menuClassName="min-w-[216px]" />
                    </div>
                ))}
                <NestedSelectList
                    searchPlaceholder="Search by name"
                    options={additionalFilters}
                    TriggerComponent={Button}
                    triggerProps={{
                        iconLeft: Icons.Tune,
                        text: 'Add Filter',
                        buttonStyle: ButtonStyles.TertiarySolid
                    }}
                    onClose={() => {
                        // nested selected list filters are not cleared on close, so we need to clear the searched items.
                        setSearchedCustomOwners([]);
                        setSearchedOwners([]);
                        setSearchedSmartPolicies([]);
                    }}
                />
            </div>
            <div className="flex gap-2">
                <Search border onChange={setSearch} placeholder={'Search account name'} />
            </div>
        </div>
    );
};

export default ServiceAccountsFilters;
